import React, { useRef } from "react";
import MultiSelect from "src/business-types/grocery/components/MultiSelect";
import CustomImage from "src/shared/Image";
import { CategoryV2 } from "src/shared/services/interfacev2";
import { ProductFormState } from "./useProductForm";
import PricingSection from "./PricingSection";
import BarcodeSection from "./BarcodeSection";
import InputBox from "src/business-types/grocery/components/InputBox";
import CustomDropdownSelect from "src/business-types/grocery/components/DropdownSelect";

type Props = {
    form: ProductFormState;
    setField: <K extends keyof ProductFormState>(key: K, val: ProductFormState[K]) => void;
    setForm: React.Dispatch<React.SetStateAction<ProductFormState>>;
    productType: "Inward" | "Service";
    category: CategoryV2[];
    uom: { id: string; uomName: string }[];
    barcodeMode: boolean;
    scanMode: boolean;
    onToggleScan: () => void;
    onBarcodeDetected: (code: string) => void;
    onImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    discountPrice: number;
    finalPrice: number;
    sellingPrice: number;
    purchasePrice: number;
    wholeSalePrice: number;
    // for edit: show server image correctly
    serverImageUrl?: string;
};

export default function ProductFormFields({
    form, setField, setForm, productType, category, uom,
    barcodeMode, scanMode, onToggleScan, onBarcodeDetected,
    onImageChange, discountPrice, finalPrice, sellingPrice, purchasePrice, wholeSalePrice,
    serverImageUrl,
}: Props) {
    const fileRef = useRef<HTMLInputElement | null>(null);


    // ── Shared renderInput ───────────────────────────────────────────────────
    const renderInput = (
        label: string,
        name: keyof ProductFormState,
        type = "text",
        mandatory = false,
        onlyNumber = false,
        disabled = false
    ) => (
        <div className="mb-3" id={label}>
            <label htmlFor={name} className="flex">
                <p className="text-sm font-medium mb-1">{label}</p>
                {mandatory && <p className="text-red-600">*</p>}
            </label>
            <InputBox
                id={name}
                type={type}
                value={form[name] as string}
                onChange={(e) => {
                    let value = e.target.value;
                    if (onlyNumber) {
                        if (!/^\d*\.?\d{0,2}$/.test(value)) return;
                    }
                    setField(name, value as any);
                }}
                onKeyDown={(e) => {
                    if (onlyNumber && ["-", "e", "E"].includes(e.key)) e.preventDefault();
                }}
                maxLength={150}
                min={onlyNumber ? 0.01 : undefined}
                onlyNumber={onlyNumber}
                disabled={disabled}
            />
        </div>
    );

    // ── Image preview logic ──────────────────────────────────────────────────
    const renderImagePreview = () => {
        if (!form.imagePreview) {
            return <span className="text-xs text-gray-400 text-center px-2">Upload Image (under 100KB)</span>;
        }
        // Edit mode: if preview still matches server URL, use CustomImage
        if (serverImageUrl && form.imagePreview === serverImageUrl) {
            return <CustomImage imagePath={form.imagePreview} />;
        }
        return <img src={form.imagePreview} alt="Preview" className="w-full h-full object-cover" />;
    };

    return (
        <div className="border border-gray-300 rounded-xl p-3 bg-gray-50 shadow-inner space-y-2">
            {/* Top row: text fields + image */}
            <div className="flex gap-2">
                <div className="w-3/5">
                    {renderInput("Product Name", "productName", "text", true)}
                    {renderInput("Second Language Name", "arabicName")}
                    <CustomDropdownSelect
                        title="Category Type"
                        name="categoryId"
                        value={form.categoryId}
                        options={category.map((c) => ({
                            _id: c._id.$oid,
                            CategoryName: c.name,
                            otherName: c.secondName,
                        }))}
                        onChange={(e) => setField("categoryId", e)}
                        mandatory={true}
                    />
                </div>
                <div className="w-2/5 flex flex-col items-center">
                    <div
                        onClick={() => fileRef.current?.click()}
                        className="cursor-pointer border border-gray-300 w-[139px] h-[139px] rounded-lg overflow-hidden flex items-center justify-center bg-white"
                    >
                        {renderImagePreview()}
                        <input ref={fileRef} type="file" accept="image/*" onChange={onImageChange} className="hidden" />
                    </div>
                    {renderInput("Product ID", "productRefId")}
                </div>
            </div>

            {/* Service: amount only */}
            {productType !== "Inward" && renderInput("Amount", "amount", "text", true)}

            {/* Inward: full pricing + barcode */}
            {productType === "Inward" && (
                <>
                    <div className="flex w-full">
                        <MultiSelect
                            title="Unit Of Measurement"
                            name="uom"
                            value={form.uom.map((u) => u.id)}
                            options={uom.map((u) => ({ _id: u.id, CategoryName: u.uomName }))}
                            onChange={(selected) =>
                                setForm((prev) => ({
                                    ...prev,
                                    uom: selected.map((s) => ({
                                        id: uom.find((u) => u.id === s)?.id || s,
                                        uomName: uom.find((u) => u.id === s)?.uomName || s,
                                    })),
                                }))
                            }
                            mandatory={true}
                        />
                    </div>

                    <div className="flex gap-2 w-full justify-between items-center">
                        <div className="w-1/2">
                            <CustomDropdownSelect
                                title="Amount of UOM"
                                name="uomBaseUnit"
                                value={form.amountMeasurement}
                                options={form.uom.map((c) => ({
                                    _id: c.id,
                                    CategoryName: c.uomName,
                                }))}
                                onChange={(e) => setField("amountMeasurement", e)}
                                mandatory={true}
                                defultSelect={true}
                            />
                        </div>
                        {renderInput("Purchase Price", "purchasePrice", "text", false, true, form.uom.length === 0)}
                    </div>

                    <PricingSection
                        discountPrice={discountPrice}
                        finalPrice={finalPrice}
                        purchasePrice={purchasePrice}
                        sellingPrice={sellingPrice}
                        discountType={form.discountType}
                        onDiscountTypeChange={(checked) => setField("discountType", checked)}
                        renderInput={renderInput}
                        uomDisabled={form.uom.length === 0}
                        wholeSalePrice={wholeSalePrice}
                    />

                    <BarcodeSection
                        barcodeId={form.barcodeId}
                        barcodeMode={barcodeMode}
                        scanMode={scanMode}
                        onToggleScan={onToggleScan}
                        onDetected={onBarcodeDetected}
                    />
                </>
            )}
        </div>
    );
}